/**
 * Workflow Collect Address Verification — main component.
 *
 * Mirrors WorkflowPhoneVerification.tsx architecture.
 * Renders an address form inside an iframe, submits to WE via _input,
 * and polls until the ensure_address inspection completes.
 *
 * States:
 * - initializing: Waiting for IDME_INIT postMessage from parent
 * - ready: Address form shown, waiting for user to submit
 * - submitting: Calling WE _input API + polling
 * - completed: WE advanced past address step, postMessage sent to parent
 * - error: Something failed
 */

import { useEffect, useState, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { submitWorkflowInput, getWorkflowExecution } from "../../shared/api/workflowEngine";
import { WorkflowCollectAddressScreen, type AddressFormData } from "./WorkflowCollectAddressScreen";
import { getAllowedDomains } from "../../shared/utils/apiConfig";
import { logger } from "../../shared/utils/logger";
import { LanguageSelector } from "../faceVerification/LanguageSelector";
import { useConsumer } from "../ConsumerProvider";
import { getConsumerConfig } from "../../consumers";
import styles from "../legalIdVerification/LegalIdVerification.module.scss";
import addressStyles from "./WorkflowCollectAddressVerification.module.scss";

export type WorkflowCollectAddressState = "initializing" | "ready" | "submitting" | "completed" | "error";

interface InitPayload {
  executionId: string;
  workflowId: number;
  inspectionName: string;
  authToken?: string;
  apiBaseUrl?: string;
  title?: string;
}

const DEFAULT_API_BASE = "https://enroll.idme.test/api/workflow";
const DEFAULT_TITLE = "Enter your most recent home address";

export function WorkflowCollectAddressVerification() {
  const { t } = useTranslation();
  const { consumer, fragnoLogoEnabled } = useConsumer();
  const consumerConfig = getConsumerConfig(consumer);
  const consumerLogo = consumerConfig?.logo;
  const themeClass = consumerConfig?.themeClass;
  const [state, setState] = useState<WorkflowCollectAddressState>("initializing");
  const [error, setError] = useState<string | null>(null);
  const [initData, setInitData] = useState<InitPayload | null>(null);
  const initDataRef = useRef<InitPayload | null>(null);
  const parentOriginRef = useRef<string>("*");

  // Derive and validate parent origin from document.referrer on mount
  useEffect(() => {
    if (!document.referrer) return;
    try {
      const origin = new URL(document.referrer).origin;
      const hostname = new URL(document.referrer).hostname;
      const isAllowed = getAllowedDomains().some(
        (domain) => hostname === domain || hostname.endsWith(`.${domain}`)
      );
      if (isAllowed) {
        parentOriginRef.current = origin;
        logger.log("[WorkflowCollectAddress] Parent origin validated:", origin);
      } else {
        logger.warn("[WorkflowCollectAddress] Parent origin not in allowlist:", origin);
      }
    } catch {
      logger.warn("[WorkflowCollectAddress] Could not parse document.referrer:", document.referrer);
    }
  }, []);

  useEffect(() => {
    initDataRef.current = initData;
  }, [initData]);

  // ── Error helper ──────────────────────────────────────────────────────
  const handleVerificationError = useCallback((errorCode: string, message: string) => {
    setError(message);
    setState("error");
    logger.error(`[WorkflowCollectAddress] Error [${errorCode}]:`, message);
    window.parent.postMessage(
      {
        source: "idme-verification",
        type: "VERIFICATION_ERROR",
        payload: { errorCode, message, executionId: initDataRef.current?.executionId },
      },
      parentOriginRef.current
    );
  }, []);

  // ── Address submit flow: _input → poll ────────────────────────────────
  const handleAddressSubmit = useCallback(
    async (address: AddressFormData) => {
      const data = initDataRef.current;
      if (!data) return;

      setState("submitting");

      const apiBase = data.apiBaseUrl || DEFAULT_API_BASE;

      try {
        logger.log("[WorkflowCollectAddress] Submitting address to WE, executionId:", data.executionId);
        await submitWorkflowInput(
          apiBase,
          data.workflowId,
          data.executionId,
          {
            inspectionName: data.inspectionName,
            input: {
              address: {
                street: address.street,
                municipality: address.municipality,
                subdivisionIso2: address.subdivisionIso2,
                postalCode: address.postalCode,
              },
            },
          },
          data.authToken
        );

        // Poll until the ensure_address inspection advances
        logger.log("[WorkflowCollectAddress] Polling until address step advances");
        const intervalMs = 2000;
        const timeoutMs = 60_000;
        const deadline = Date.now() + timeoutMs;

        const isAddressStepActive = (exec: Awaited<ReturnType<typeof getWorkflowExecution>>) =>
          exec.status === "PENDING_ASYNC" ||
          exec.status === "RUNNING" ||
          exec.status === "PENDING" ||
          (exec.status === "WAITING_FOR_INPUT" && exec.currentInspection === data.inspectionName);

        let exec = await getWorkflowExecution(apiBase, data.workflowId, data.executionId, data.authToken);

        while (isAddressStepActive(exec) && Date.now() < deadline) {
          logger.log("[WorkflowCollectAddress] Still", exec.status, `(${exec.currentInspection ?? "?"}) — polling`);
          await new Promise((r) => setTimeout(r, intervalMs));
          exec = await getWorkflowExecution(apiBase, data.workflowId, data.executionId, data.authToken);
        }

        if (Date.now() >= deadline) {
          handleVerificationError("poll_timeout", "Verification timed out waiting for address step");
          return;
        }

        logger.log("[WorkflowCollectAddress] Address step done, status:", exec.status, `(${exec.currentInspection ?? "?"})`);

        if (exec.status === "FAILED" || exec.status === "CANCELLED") {
          const msg =
            (exec.result?.data as Record<string, unknown>)?.message as string ??
            "Verification failed";
          handleVerificationError("poll_failed", msg);
          return;
        }

        setState("completed");
      } catch (err) {
        logger.error("[WorkflowCollectAddress] Submit flow failed:", err);
        handleVerificationError(
          "submit_failed",
          err instanceof Error ? err.message : "Verification processing failed"
        );
      }
    },
    [handleVerificationError]
  );

  // ── No US address: signal parent ──────────────────────────────────────
  const handleNoUsAddress = useCallback(() => {
    const data = initDataRef.current;
    logger.log("[WorkflowCollectAddress] User has no US address");
    window.parent.postMessage(
      {
        source: "idme-verification",
        type: "VERIFICATION_NO_US_ADDRESS",
        payload: { executionId: data?.executionId, workflowId: data?.workflowId },
      },
      parentOriginRef.current
    );
  }, []);

  // ── Step 1: Send IDME_READY, listen for IDME_INIT ─────────────────────
  useEffect(() => {
    window.parent.postMessage(
      { source: "idme-verification", type: "IDME_READY" },
      parentOriginRef.current
    );

    const handler = (event: MessageEvent) => {
      if (!event.data || event.data.type !== "IDME_INIT") return;
      try {
        const senderHostname = new URL(event.origin).hostname;
        const allowed = getAllowedDomains().some(
          (d) => senderHostname === d || senderHostname.endsWith(`.${d}`)
        );
        if (!allowed) {
          logger.warn("[WorkflowCollectAddress] IDME_INIT rejected from origin:", event.origin);
          return;
        }
      } catch {
        logger.warn("[WorkflowCollectAddress] Could not parse IDME_INIT origin:", event.origin);
        return;
      }
      const payload = event.data.payload as InitPayload;
      logger.log("[WorkflowCollectAddress] Received init:", payload.executionId);
      setInitData(payload);
      initDataRef.current = payload;
      setState("ready");
    };

    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, []);

  // ── Step 2: Send VERIFICATION_COMPLETE when step completes ────────────
  useEffect(() => {
    if (state !== "completed" || !initData) return;
    logger.log("[WorkflowCollectAddress] Notifying parent of completion");
    window.parent.postMessage(
      {
        source: "idme-verification",
        type: "VERIFICATION_COMPLETE",
        payload: {
          executionId: initData.executionId,
          workflowId: initData.workflowId,
          status: "completed",
        },
      },
      parentOriginRef.current
    );
  }, [state, initData]);

  // ── Render ────────────────────────────────────────────────────────────
  return (
    <div className={[styles.pageContainer, themeClass].filter(Boolean).join(" ")}>
      <div className={styles.container}>

        {/* VEX-7983: with the Fragno logo on, BrandLayout renders the shared header instead */}
        {!fragnoLogoEnabled && (
          <header className={styles.logoContainer}>
            <div className={styles.logo}>
              <img src="/assets/svg/idme-logo.svg" alt="IDMe" />
            </div>
            {consumerLogo && (
              <>
                <span className={styles.logoSeparator} aria-label="and">+</span>
                <div className={styles.logo}>
                  <img src={consumerLogo.src} alt={consumerLogo.alt} />
                </div>
              </>
            )}
          </header>
        )}

        <main className={styles.contentContainer}>

          {/* Initializing */}
          {state === "initializing" && (
            <div className={styles.card}>
              <div className={styles.spinner} />
            </div>
          )}

          {/* Ready — address entry form */}
          {state === "ready" && (
            <WorkflowCollectAddressScreen
              title={initData?.title ?? DEFAULT_TITLE}
              onSubmit={handleAddressSubmit}
              onNoUsAddress={handleNoUsAddress}
              isSubmitting={false}
            />
          )}

          {/* Submitting — spinner */}
          {state === "submitting" && (
            <div className={styles.card}>
              <h2 className={styles.title}>{t("collectAddress.processing.title")}</h2>
              <div className={styles.spinner} />
            </div>
          )}

          {/* Completed */}
          {state === "completed" && (
            <div className={styles.card}>
              <div className={styles.successIcon}>✓</div>
              <h2 className={styles.title}>{t("collectAddress.completed.title")}</h2>
              <p className={styles.subtitle}>{t("collectAddress.completed.subtitle")}</p>
            </div>
          )}

          {/* Error */}
          {state === "error" && (
            <div className={styles.card}>
              <div className={styles.errorIcon}>✕</div>
              <h2 className={styles.title}>{t("collectAddress.error.title")}</h2>
              <p className={styles.error}>{error}</p>
            </div>
          )}

        </main>

        <footer className={addressStyles.footer}>
          <LanguageSelector />
          <div className={addressStyles.footerLinks}>
            <a
              href="https://www.id.me/about"
              target="_blank"
              rel="noopener noreferrer"
              className={addressStyles.footerLink}
              aria-label={t("collectAddress.footer.whatIsIdmeAriaLabel")}
            >
              {t("collectAddress.footer.whatIsIdme")}
            </a>
            <span aria-hidden="true" className={addressStyles.footerDivider}>|</span>
            <a
              href="https://www.id.me/terms"
              target="_blank"
              rel="noopener noreferrer"
              className={addressStyles.footerLink}
              aria-label={t("collectAddress.footer.termsOfServiceAriaLabel")}
            >
              {t("collectAddress.footer.termsOfService")}
            </a>
            <span aria-hidden="true" className={addressStyles.footerDivider}>|</span>
            <a
              href="https://www.id.me/privacy"
              target="_blank"
              rel="noopener noreferrer"
              className={addressStyles.footerLink}
              aria-label={t("collectAddress.footer.privacyPolicyAriaLabel")}
            >
              {t("collectAddress.footer.privacyPolicy")}
            </a>
          </div>
        </footer>

      </div>
    </div>
  );
}
